import React from 'react';
import { Alert, Autocomplete, Box, Button, Chip, Collapse, LinearProgress, Link, Stack, TextField, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import type { FunnelInsightsApiClient } from '../../api/insightsClient';
import type {
  FunnelCreativeDiagnosticsRow,
  FunnelCreativePerformanceRow,
  FunnelReportsCreativesData,
} from '../../types';
import { CREATIVE_DECISIONS_TEST_IDS } from '../../testIds';
import { metaPurchasesCopy } from '../../metaPurchasesCopy';
import { PltvWithCac, cacBesideFromRow } from '../PltvWithCac';
import { DataThroughCaption } from '../DataThroughCaption';
import {
  applyPaidFacetFilters,
  PAID_FACET_KEYS,
  type PaidFacetFilters,
  type PaidFacetKey,
} from '../../serving/paidFacets';
import {
  DenseTable,
  EvidenceStack,
  FunnelQualityCell,
  MATCH_QUALITY_LABELS,
  RateEvidenceCell,
  RoasValue,
  WatchOnlyPltvChip,
  creativeLabel,
  destinationEvidenceLabel,
  formatCurrency,
  formatInteger,
  formatNullableCurrency,
  issueFlagsLabel,
  matchQualityLabel,
  metaDeliveryLabel,
  nextSortState,
  sortableRate,
  type EvidenceColumn,
  type EvidenceRow,
  type SortState,
} from '../FunnelReportsDashboard';

/**
 * W-R6 (DISC-AC-149/150) — the CD saved views that replace the standalone
 * Reports sections. Column sets, formats, tooltips and states reproduce the
 * DISC-PRE-021 parity inventory 1:1 (deviations are declared there, never
 * silent). Both views fetch through the standalone creatives read API
 * (`reports/creatives?view=…`) — each view runs ONLY its own serving lane.
 */

interface SavedViewProps {
  client: FunnelInsightsApiClient;
  projectId: string;
  /** Date-only strings from the CD page range picker. */
  from: string;
  to: string;
}

/** CD page range picker emits date-only values; the API expects ISO instants
 * with an EXCLUSIVE `to` (same convention as the portfolio request). */
function windowToInstants(from: string, to: string): { from: string; to: string } {
  return {
    from: `${from}T00:00:00.000Z`,
    to: `${to}T00:00:00.000Z`,
  };
}

function useCreativesView(
  props: SavedViewProps,
  view: 'performance' | 'diagnostics',
): { data: FunnelReportsCreativesData | null; loading: boolean; error: string | null } {
  const [data, setData] = React.useState<FunnelReportsCreativesData | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const { client, projectId, from, to } = props;

  React.useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    const window = windowToInstants(from, to);
    client.getReportsCreatives({
      projectId,
      from: window.from,
      to: window.to,
      view,
      signal: controller.signal,
    }).then(response => {
      setData(response.data);
      setLoading(false);
    }).catch((requestError: unknown) => {
      if (controller.signal.aborted) return;
      setError(requestError instanceof Error ? requestError.message : 'Failed to load the view.');
      setLoading(false);
    });
    return () => controller.abort();
  }, [client, projectId, from, to, view]);

  return { data, loading, error };
}

/** alias → onboardingId from the funnel directory (1 mart job + identity). */
function useAliasOnboardingMap(props: SavedViewProps): Map<string, string> {
  const [map, setMap] = React.useState<Map<string, string>>(new Map());
  const { client, projectId, from, to } = props;
  React.useEffect(() => {
    const controller = new AbortController();
    const window = windowToInstants(from, to);
    client.getReportsFunnels({
      projectId,
      from: window.from,
      to: window.to,
      signal: controller.signal,
    }).then(response => {
      const next = new Map<string, string>();
      for (const funnel of response.data.funnels ?? []) {
        for (const placement of funnel.identity?.placements ?? []) {
          if (placement.alias && !next.has(placement.alias)) {
            next.set(placement.alias, funnel.onboardingId);
          }
        }
      }
      setMap(next);
    }).catch(() => {
      // The funnel link is an enrichment — its absence degrades the Links
      // column to Image/Preview, never the whole view.
    });
    return () => controller.abort();
  }, [client, projectId, from, to]);
  return map;
}

/**
 * W-R6 I2 step 0 — paid facet filters (PRE-021 §1f/§2f parity, Story B-13).
 * CLIENT-side narrowing of the loaded rows through the ONE shared contract
 * (`serving/paidFacets.ts` `applyPaidFacetFilters`) — never a re-implementation.
 * Semantics are identical to the deleted surfaces: both the old server
 * post-pass and this filter operate AFTER the SQL row cap (post-LIMIT set).
 * The applies/not-applied matrix is the declared contract: performance = 5
 * facets (metaAdsetId/matchQuality declared-not-applied); diagnostics = all 7.
 */
interface FacetField {
  key: PaidFacetKey;
  label: string;
}

const PERFORMANCE_FACET_FIELDS: FacetField[] = [
  { key: 'campaign', label: 'Campaign' },
  { key: 'metaAdAccountId', label: 'Meta ad account' },
  { key: 'metaCampaignId', label: 'Meta campaign' },
  { key: 'metaAdId', label: 'Meta ad' },
  { key: 'metaCreativeId', label: 'Meta creative' },
];

const DIAGNOSTICS_FACET_FIELDS: FacetField[] = [
  { key: 'campaign', label: 'Campaign' },
  { key: 'metaAdAccountId', label: 'Meta ad account' },
  { key: 'metaCampaignId', label: 'Meta campaign' },
  { key: 'metaAdsetId', label: 'Meta ad set' },
  { key: 'metaAdId', label: 'Meta ad' },
  { key: 'metaCreativeId', label: 'Meta creative' },
];

/** Linkage `evidence_kind` vocabulary served by the diagnostics lane
 * (`CreativeDiagnosticsLinkageQuery`); offered ALONGSIDE the legacy session
 * match tiers through the transition. Substring matching keeps a legacy
 * `utm_tuple` selection narrowing to `validated_utm_tuple` rows. */
const LINKAGE_EVIDENCE_KINDS = [
  'provider_entity_id',
  'gclid',
  'gbraid',
  'wbraid',
  'dclid',
  'ttclid',
  'fbclid',
  'validated_utm_tuple',
  'other_evidence',
];

const MATCH_QUALITY_FILTER_OPTIONS = [
  ...Object.keys(MATCH_QUALITY_LABELS),
  ...LINKAGE_EVIDENCE_KINDS.filter(kind => !(kind in MATCH_QUALITY_LABELS)),
];

function activeFacetCount(filters: PaidFacetFilters): number {
  return PAID_FACET_KEYS.filter(key => Boolean(filters[key]?.trim())).length;
}

function FiltersToggleButton({ open, onToggle, count, testId }: {
  open: boolean;
  onToggle: () => void;
  count: number;
  testId: string;
}): React.ReactElement {
  return (
    <Button
      size="small"
      variant={open || count > 0 ? 'outlined' : 'text'}
      color="inherit"
      onClick={onToggle}
      data-testid={testId}
      sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
    >
      {count > 0 ? `Filters (${count})` : 'Filters'}
    </Button>
  );
}

function FacetFiltersPanel({ open, fields, withMatchQuality, filters, onChange, filterTestId }: {
  open: boolean;
  fields: FacetField[];
  withMatchQuality?: boolean;
  filters: PaidFacetFilters;
  onChange: (next: PaidFacetFilters) => void;
  filterTestId: (facet: string) => string;
}): React.ReactElement {
  const matchQualityValue = filters.matchQuality && MATCH_QUALITY_FILTER_OPTIONS.includes(filters.matchQuality)
    ? filters.matchQuality
    : null;
  return (
    <Collapse in={open}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))',
          gap: 1.5,
          mb: 1.5,
          pt: 0.5,
        }}
      >
        {fields.map(field => (
          <TextField
            key={field.key}
            data-testid={filterTestId(field.key)}
            label={field.label}
            size="small"
            fullWidth
            value={filters[field.key] ?? ''}
            onChange={event => onChange({ ...filters, [field.key]: event.target.value })}
          />
        ))}
        {withMatchQuality && (
          <Autocomplete
            data-testid={filterTestId('matchQuality')}
            options={MATCH_QUALITY_FILTER_OPTIONS}
            groupBy={option => (option in MATCH_QUALITY_LABELS ? 'Session match (legacy)' : 'Linkage evidence')}
            getOptionLabel={option => MATCH_QUALITY_LABELS[option] ?? option}
            value={matchQualityValue}
            onChange={(_event, selected) => onChange({ ...filters, matchQuality: selected || undefined })}
            fullWidth
            renderInput={params => <TextField {...params} label="Match quality" size="small" />}
          />
        )}
      </Box>
    </Collapse>
  );
}

const PERFORMANCE_COLUMNS: EvidenceColumn[] = [
  { id: 'creative', label: 'Creative', sortable: true, minWidth: 200 },
  { id: 'campaign', label: 'Campaign', sortable: true, minWidth: 170 },
  { id: 'spend', label: 'Spend', sortable: true, align: 'right' },
  {
    id: 'watchPltv',
    label: 'Watch-only pLTV (12m)',
    sortable: true,
    align: 'right',
    minWidth: 260,
    description: 'Gross 12-month forecast per subscriber for this exact Meta ad deployment. Costs are not included. Watch-only and never a kill/scale input.',
  },
  { id: 'results', label: 'Results (ours)', sortable: true, align: 'right', minWidth: 120, description: 'Purchases from OUR matched sessions at true ad grain; offers below.' },
  { id: 'revenue', label: 'Revenue', sortable: true, align: 'right', description: "The ad's covered purchase revenue from our sessions." },
  { id: 'roas', label: 'ROAS', sortable: true, align: 'right', description: "The ad's covered revenue ÷ the ad's own spend." },
  {
    id: 'quality',
    label: 'Early-funnel diagnostic',
    sortable: true,
    align: 'right',
    minWidth: 140,
    description: "Deterministic 0–100 composite of the ad traffic's behavior vs the account baseline: paywall reach + quiz depth + buy-click rate. n/a under 100 sessions. ≥60 above baseline · 40–59 mixed · <40 below baseline. Observational only — not a validated surrogate or an economic verdict.",
  },
  { id: 'impressions', label: 'Impr.', sortable: true, align: 'right' },
  { id: 'clicks', label: 'Clicks', sortable: true, align: 'right' },
  // People-attribution S3 (AC-022): the «People clicked» column is gone — a
  // SUM of per-day Meta unique_clicks is not a distinct-people count.
  { id: 'ctr', label: 'CTR', sortable: true, align: 'right' },
  { id: 'cpc', label: 'CPC', sortable: true, align: 'right' },
  { id: 'metaResults', label: 'Meta results', sortable: true, minWidth: 130, description: "Meta's own attribution (pixel/SKAN): installs, purchases in the 7-day-click bucket (like-for-like with ours) and purchase value credited to this creative; view-through purchases are listed separately because we cannot observe them. Independent of Segmently session matching." },
  { id: 'links', label: 'Links', minWidth: 170 },
];

export function CreativePerformanceSavedView(props: SavedViewProps): React.ReactElement {
  const { data, loading, error } = useCreativesView(props, 'performance');
  const aliasOnboarding = useAliasOnboardingMap(props);
  const [matchedOnly, setMatchedOnly] = React.useState(true);
  const [filters, setFilters] = React.useState<PaidFacetFilters>({});
  const [filtersOpen, setFiltersOpen] = React.useState(false);
  const [sortState, setSortState] = React.useState<SortState>({ columnId: 'spend', direction: 'desc' });

  if (loading) {
    return <LinearProgress data-testid={CREATIVE_DECISIONS_TEST_IDS.performanceLoading} aria-label="Loading creative performance" />;
  }
  if (error) {
    return <Alert severity="error" data-testid={CREATIVE_DECISIONS_TEST_IDS.performanceError}>{error}</Alert>;
  }
  const rows = data?.creativePerformance?.rows ?? [];
  if (rows.length === 0) {
    return (
      <Alert severity="info" data-testid={CREATIVE_DECISIONS_TEST_IDS.performanceEmpty}>
        No rows for the selected scope and time window.
      </Alert>
    );
  }

  const filteredRows = applyPaidFacetFilters('creativePerformance', rows, filters);
  const adGrainRows = filteredRows.filter(row => row.adSessions !== undefined);
  const matchedOnlyApplicable = adGrainRows.length > 0 && adGrainRows.length < filteredRows.length;
  const visibleRows = matchedOnly && matchedOnlyApplicable ? adGrainRows : filteredRows;
  const hiddenCount = filteredRows.length - adGrainRows.length;

  const evidenceRows: EvidenceRow[] = visibleRows.map((row, index) => ({
    key: row.adId ?? row.creativeId ?? index,
    cells: performanceCells(row, index, props.projectId, aliasOnboarding),
    sortValues: {
      creative: row.creativeName ?? row.creativeId ?? row.adId ?? '',
      campaign: row.campaignName ?? row.campaignId ?? '',
      spend: row.spend ?? 0,
      watchPltv: row.watchOnlyPltv?.state === 'available' ? row.watchOnlyPltv.p50Minor ?? null : null,
      results: row.adPurchaseJourneys ?? null,
      revenue: row.adRevenueCovered ?? null,
      roas: row.adRoasCovered ?? null,
      quality: row.funnelQualityScore ?? null,
      impressions: row.impressions ?? 0,
      clicks: row.clicks ?? 0,
      ctr: row.ctr ?? -1,
      cpc: row.cpc ?? Number.MAX_SAFE_INTEGER,
      // S3: sort Meta results by the like-for-like bucket when served.
      metaResults: row.metaPurchases7dClick ?? row.metaPurchases ?? 0,
    },
  }));

  return (
    <Box data-testid={CREATIVE_DECISIONS_TEST_IDS.performanceView}>
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        {matchedOnlyApplicable && (
          <Chip
            size="small"
            variant={matchedOnly ? 'filled' : 'outlined'}
            color={matchedOnly ? 'primary' : 'default'}
            label="Matched to funnels only"
            onClick={() => setMatchedOnly(current => !current)}
            data-testid={CREATIVE_DECISIONS_TEST_IDS.performanceMatchedOnlyToggle}
          />
        )}
        {matchedOnly && matchedOnlyApplicable && hiddenCount > 0 && (
          <Typography variant="caption" color="text.secondary">
            {`Hiding ${hiddenCount} creatives without matched sessions (App Store / untagged delivery).`}
          </Typography>
        )}
        <FiltersToggleButton
          open={filtersOpen}
          onToggle={() => setFiltersOpen(current => !current)}
          count={activeFacetCount(filters)}
          testId={CREATIVE_DECISIONS_TEST_IDS.performanceFiltersToggle}
        />
        <Box sx={{ flex: 1 }} />
        <DataThroughCaption
          dataThrough={data?.creativePerformance?.dataThrough}
          testId={`${CREATIVE_DECISIONS_TEST_IDS.performanceView}-data-through`}
        />
      </Stack>
      <FacetFiltersPanel
        open={filtersOpen}
        fields={PERFORMANCE_FACET_FIELDS}
        filters={filters}
        onChange={setFilters}
        filterTestId={CREATIVE_DECISIONS_TEST_IDS.performanceFilter}
      />
      {filteredRows.length === 0 ? (
        <Alert severity="info" data-testid={CREATIVE_DECISIONS_TEST_IDS.performanceFilteredEmpty}>
          No loaded rows match the active filters.
        </Alert>
      ) : (
        <>
          <Box sx={{ maxHeight: 560, overflow: 'auto', position: 'relative' }}>
            <DenseTable
              columns={PERFORMANCE_COLUMNS}
              rows={evidenceRows}
              sortState={sortState}
              onSort={(columnId) => setSortState(current => nextSortState(current, columnId))}
            />
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Top creatives by spend for the report window. CTR = clicks / impressions, CPC = spend / clicks.
            Watch-only pLTV is gross per subscriber, excludes costs, and never drives a kill/scale decision.
            Preview opens the Meta ad; Open funnel opens the matched onboarding in a new tab.
          </Typography>
        </>
      )}
    </Box>
  );
}

function performanceCells(
  row: FunnelCreativePerformanceRow,
  index: number,
  projectId: string,
  aliasOnboarding: Map<string, string>,
): React.ReactNode[] {
  const alias = row.destinationAlias || row.sessionAlias;
  const onboardingId = alias ? aliasOnboarding.get(alias) : undefined;
  const onboardingHref = onboardingId
    ? `/project/${encodeURIComponent(projectId)}/onboarding-v2/${encodeURIComponent(onboardingId)}?insights=1`
    : undefined;
  const unmatched = row.adSessions === undefined;
  const dash = <Typography variant="body2" color="text.disabled">—</Typography>;
  return [
    <Box key="creative" sx={{ display: 'flex', gap: 1, minWidth: 0 }} data-testid={CREATIVE_DECISIONS_TEST_IDS.performanceRow(index)}>
      {(row.thumbnailUrl || row.imageUrl) && (
        <Box
          component="img"
          src={row.thumbnailUrl || row.imageUrl}
          alt=""
          onError={(event: React.SyntheticEvent<HTMLImageElement>) => { event.currentTarget.style.display = 'none'; }}
          sx={{ width: 32, height: 32, borderRadius: 0.5, objectFit: 'cover', flexShrink: 0 }}
        />
      )}
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ overflowWrap: 'anywhere', fontWeight: 500 }}>
          {row.creativeName ?? row.creativeId ?? row.adId ?? 'n/a'}
        </Typography>
        {(row.creativeTitle || row.creativeBody) && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflowWrap: 'anywhere' }}>
            {row.creativeTitle || row.creativeBody}
          </Typography>
        )}
      </Box>
    </Box>,
    row.campaignName || row.campaignId || '(not set)',
    row.spend !== undefined ? formatCurrency(row.spend) : 'n/a',
    // S7 / D-J: the watch-only band never renders without CAC beside it.
    <PltvWithCac
      key="chip"
      cac={cacBesideFromRow(row)}
      testId={`${CREATIVE_DECISIONS_TEST_IDS.performanceRow(index)}-cac`}
      pltv={(
        <WatchOnlyPltvChip
          watchOnly={row.watchOnlyPltv}
          projectId={projectId}
          enabled
          testId={`${CREATIVE_DECISIONS_TEST_IDS.performanceRow(index)}-watch-only`}
        />
      )}
    />,
    unmatched ? dash : (
      <Box key="results">
        <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatInteger(row.adPurchaseJourneys ?? 0)}</Typography>
        <Typography variant="caption" color="text.secondary">{`${formatInteger(row.adOfferJourneys ?? 0)} offers`}</Typography>
      </Box>
    ),
    unmatched ? dash : formatNullableCurrency(row.adRevenueCovered),
    unmatched ? dash : (
      <RoasValue key="roas" value={row.adRoasCovered} title="The ad's own covered revenue ÷ the ad's own spend." />
    ),
    <FunnelQualityCell key="quality" row={row} testIdBadge={`${CREATIVE_DECISIONS_TEST_IDS.performanceRow(index)}-clickbait`} />,
    row.impressions !== undefined ? formatInteger(row.impressions) : 'n/a',
    row.clicks !== undefined ? formatInteger(row.clicks) : 'n/a',
    row.ctr !== undefined && row.ctr !== null ? `${(row.ctr * 100).toFixed(2)}%` : 'n/a',
    formatNullableCurrency(row.cpc),
    <EvidenceStack
      key="meta"
      entries={[
        { label: 'Installs', value: row.metaInstalls !== undefined ? formatInteger(row.metaInstalls) : 'n/a' },
        // S3 (D-F, AC-021): the 7-day-click bucket is the comparable number;
        // view-through is its own line, only when > 0; a pre-S3 payload keeps
        // the umbrella total under its honest «all windows» name.
        {
          label: metaPurchasesCopy(row).basis === 'seven_day_click' ? 'Purchases (7-day click)' : 'Purchases (all windows)',
          value: row.metaPurchases !== undefined || row.metaPurchases7dClick !== undefined
            ? formatInteger(metaPurchasesCopy(row).basis === 'seven_day_click' ? row.metaPurchases7dClick ?? 0 : row.metaPurchases ?? 0)
            : 'n/a',
        },
        ...(metaPurchasesCopy(row).viewThrough
          ? [{ label: 'View-through', value: `${formatInteger(row.metaPurchasesViewThrough ?? 0)} · not observable` }]
          : []),
        { label: 'Value', value: row.metaPurchaseValue ? formatCurrency(row.metaPurchaseValue) : 'n/a' },
      ]}
    />,
    <Stack key="links" direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      {row.imageUrl && (
        <Link href={row.imageUrl} target="_blank" rel="noopener" variant="body2" sx={{ whiteSpace: 'nowrap' }}>
          Image <OpenInNewIcon sx={{ fontSize: 12, verticalAlign: 'middle' }} />
        </Link>
      )}
      {row.previewLink && (
        <Link
          href={row.previewLink}
          target="_blank"
          rel="noopener"
          variant="body2"
          title="Opens Meta's ad preview — requires a Facebook login with access to the ad account."
          sx={{ whiteSpace: 'nowrap' }}
        >
          Preview (Meta login) <OpenInNewIcon sx={{ fontSize: 12, verticalAlign: 'middle' }} />
        </Link>
      )}
      {onboardingHref && (
        <Link href={onboardingHref} target="_blank" rel="noopener" variant="body2" sx={{ whiteSpace: 'nowrap' }}>
          Open funnel <OpenInNewIcon sx={{ fontSize: 12, verticalAlign: 'middle' }} />
        </Link>
      )}
    </Stack>,
  ];
}

const DIAGNOSTICS_COLUMNS: EvidenceColumn[] = [
  { id: 'creative', label: 'Creative', sortable: true, minWidth: 190 },
  { id: 'campaign', label: 'Campaign', sortable: true, minWidth: 150 },
  { id: 'match', label: 'Match', sortable: true, minWidth: 116 },
  { id: 'firstScreen', label: 'First screen', sortable: true, align: 'right', minWidth: 116 },
  { id: 'firstToSecond', label: 'First -> second', sortable: true, align: 'right', minWidth: 120 },
  { id: 'spend', label: 'Spend', sortable: true, align: 'right' },
  { id: 'purchases', label: 'Purchases', sortable: true, minWidth: 130, description: 'Journeys with a completed purchase (count; the rate is Purchase conv).' },
  { id: 'costFirstSecond', label: 'Cost / first -> second', sortable: true, align: 'right', minWidth: 140 },
  { id: 'delivery', label: 'Delivery', sortable: true, minWidth: 140 },
  { id: 'destination', label: 'Destination', minWidth: 190 },
  { id: 'flags', label: 'Flags', sortable: true, minWidth: 150 },
];

export function CreativeDiagnosticsSavedView(props: SavedViewProps): React.ReactElement {
  const { data, loading, error } = useCreativesView(props, 'diagnostics');
  const [filters, setFilters] = React.useState<PaidFacetFilters>({});
  const [filtersOpen, setFiltersOpen] = React.useState(false);
  const [sortState, setSortState] = React.useState<SortState>({ columnId: 'spend', direction: 'desc' });

  if (loading) {
    return <LinearProgress data-testid={CREATIVE_DECISIONS_TEST_IDS.diagnosticsLoading} aria-label="Loading creative diagnostics" />;
  }
  if (error) {
    return <Alert severity="error" data-testid={CREATIVE_DECISIONS_TEST_IDS.diagnosticsError}>{error}</Alert>;
  }
  const rows = data?.creativeDiagnostics ?? [];
  const filteredRows = applyPaidFacetFilters('creativeDiagnostics', rows, filters);

  return (
    <Box data-testid={CREATIVE_DECISIONS_TEST_IDS.diagnosticsView}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
        {/* DISC-AC-150: scope is structurally `all` — no scope control exists on
            this view; the caption states the property instead. */}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block' }}
          data-testid={CREATIVE_DECISIONS_TEST_IDS.diagnosticsScopeNote}
        >
          All deployments — including creatives with no funnel match. Unmatched rows are the point of this view.
        </Typography>
        <Box sx={{ flex: 1 }} />
        <FiltersToggleButton
          open={filtersOpen}
          onToggle={() => setFiltersOpen(current => !current)}
          count={activeFacetCount(filters)}
          testId={CREATIVE_DECISIONS_TEST_IDS.diagnosticsFiltersToggle}
        />
      </Stack>
      <FacetFiltersPanel
        open={filtersOpen}
        fields={DIAGNOSTICS_FACET_FIELDS}
        withMatchQuality
        filters={filters}
        onChange={setFilters}
        filterTestId={CREATIVE_DECISIONS_TEST_IDS.diagnosticsFilter}
      />
      {rows.length === 0 ? (
        <Alert severity="info" data-testid={CREATIVE_DECISIONS_TEST_IDS.diagnosticsEmpty}>
          No rows for the selected scope and time window.
        </Alert>
      ) : filteredRows.length === 0 ? (
        <Alert severity="info" data-testid={CREATIVE_DECISIONS_TEST_IDS.diagnosticsFilteredEmpty}>
          No loaded rows match the active filters.
        </Alert>
      ) : (
        <Box sx={{ maxHeight: 560, overflow: 'auto', position: 'relative' }}>
          <DenseTable
            columns={DIAGNOSTICS_COLUMNS}
            rows={filteredRows.map((row, index) => diagnosticsEvidenceRow(row, index))}
            sortState={sortState}
            onSort={(columnId) => setSortState(current => nextSortState(current, columnId))}
          />
        </Box>
      )}
    </Box>
  );
}

function diagnosticsEvidenceRow(row: FunnelCreativeDiagnosticsRow, index: number): EvidenceRow {
  return {
    key: row.metaAdId ?? `${row.campaign}-${index}`,
    cells: [
      <Box key="creative" data-testid={CREATIVE_DECISIONS_TEST_IDS.diagnosticsRow(index)}>
        <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{creativeLabel(row)}</Typography>
      </Box>,
      row.campaign,
      <Chip
        key="match"
        size="small"
        variant="outlined"
        color={(row.matchQuality ?? 'none') === 'none' ? 'warning' : 'default'}
        label={matchQualityLabel(row.matchQuality)}
      />,
      <RateEvidenceCell
        key="firstScreen"
        rate={row.firstScreenOpenRate ?? null}
        numerator={row.firstScreenOpenedJourneys ?? 0}
        denominator={row.startedJourneys}
      />,
      <RateEvidenceCell
        key="firstToSecond"
        rate={row.firstToSecondRate ?? null}
        numerator={row.firstToSecondJourneys ?? 0}
        denominator={row.firstScreenOpenedJourneys ?? 0}
      />,
      row.spend === null && Number(row.unmatchedSpend ?? 0) > 0 ? (
        <Box key="spend">
          <Typography variant="body2" color="text.disabled">n/a</Typography>
          <Typography variant="caption" color="text.secondary">{`unmatched ${formatCurrency(Number(row.unmatchedSpend))}`}</Typography>
        </Box>
      ) : formatNullableCurrency(row.spend),
      <EvidenceStack
        key="purchases"
        entries={[
          { label: 'Purchases', value: formatInteger(row.purchaseJourneys), emphasis: true },
          { label: 'Cost / purchase', value: formatNullableCurrency(row.costPerPurchase) },
        ]}
      />,
      formatNullableCurrency(row.costPerFirstToSecond),
      metaDeliveryLabel(row),
      destinationEvidenceLabel(row),
      issueFlagsLabel(row.issueFlags),
    ],
    sortValues: {
      creative: creativeLabel(row),
      campaign: row.campaign,
      match: row.matchQuality || 'none',
      firstScreen: sortableRate(row.firstScreenOpenRate ?? null, row.firstScreenOpenedJourneys ?? 0, row.startedJourneys),
      firstToSecond: sortableRate(row.firstToSecondRate ?? null, row.firstToSecondJourneys ?? 0, row.firstScreenOpenedJourneys ?? 0),
      spend: row.spend ?? 0,
      purchases: row.purchaseJourneys,
      costFirstSecond: row.costPerFirstToSecond ?? null,
      delivery: row.impressions ?? 0,
      flags: issueFlagsLabel(row.issueFlags),
    },
  };
}
